import { useEffect, useState, type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Check, ChevronLeft, Copy } from "lucide-react";
import { DeliverySummary } from "~/components/delivery-summary";
import { Notice } from "~/components/notice";
import { ParcelChat } from "~/components/parcel-chat";
import { ParcelProgress } from "~/components/parcel-progress";
import { PaySheet } from "~/components/pay-sheet";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { api } from "~/lib/api";
import { useCopy } from "~/lib/clipboard";
import { formatDate, formatDay, formatKES, toNum } from "~/lib/format";
import {
  countryName,
  isPayable,
  parcelTitle,
  routeLabel,
  statusDetail,
  statusHeadline,
  timelineLabel,
  upcomingEta,
} from "~/lib/parcel-status";
import type { IParcel } from "~/lib/proto";
import { cn } from "~/lib/utils";

export const Route = createFileRoute("/_authed/_portal/parcel/$id")({
  component: ParcelDetail,
});

function ParcelDetail() {
  const { id } = Route.useParams();
  const parcel = useQuery({ queryKey: ["parcel", id], queryFn: () => api.getParcel(id) });

  return (
    <div className="space-y-5">
      <Link to="/" className="text-muted-foreground hover:text-foreground -ml-1 inline-flex min-h-11 items-center gap-1 text-[15px] font-medium">
        <ChevronLeft className="size-5" /> Parcels
      </Link>

      {parcel.isLoading ? (
        <div className="space-y-4" aria-busy>
          <Skeleton className="h-9 w-2/3 rounded-lg" />
          <Skeleton className="h-44 rounded-xl" />
          <Skeleton className="h-20 rounded-xl" />
        </div>
      ) : !parcel.data ? (
        <Notice title="We could not open this parcel" body="It may have been removed, or your connection dropped.">
          <Button variant="outline" className="h-11 px-5 text-[15px]" onClick={() => void parcel.refetch()}>
            Try again
          </Button>
        </Notice>
      ) : (
        <ParcelView parcel={parcel.data} />
      )}
    </div>
  );
}

function ParcelView({ parcel }: { parcel: IParcel }) {
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (window.location.hash === "#messages") document.getElementById("messages")?.scrollIntoView({ block: "start" });
  }, []);

  return (
    <>
      <header>
        <h1 className="type-title wrap-anywhere">{parcelTitle(parcel)}</h1>
        <p className="text-muted-foreground type-body mt-1">{routeLabel(parcel)}</p>
      </header>

      <StatusCard parcel={parcel} onPay={() => setPaying(true)} />
      <DeliverySummary parcel={parcel} />
      <div id="messages" className="scroll-mt-20">
        <ParcelChat parcelId={parcel.id!} />
      </div>
      <Timeline parcel={parcel} />
      <Details parcel={parcel} />

      <PaySheet open={paying} onOpenChange={setPaying} parcels={[parcel]} />
    </>
  );
}

function StatusCard({ parcel, onPay }: { parcel: IParcel; onPay: () => void }) {
  const eta = upcomingEta(parcel);
  const held = parcel.status === "ON_HOLD";
  return (
    <section className={cn("rounded-xl border p-5", held ? "border-warning/60 bg-warning/10" : "border-hairline bg-card")}>
      <ParcelProgress status={parcel.status} />
      <h2 className="type-headline mt-5 text-[20px]">{statusHeadline(parcel)}</h2>
      <p className="text-muted-foreground type-body mt-1">{statusDetail(parcel)}</p>

      {eta && (
        <p className="type-body mt-3">
          <span className="text-muted-foreground">Estimated delivery </span>
          <span className="font-medium">{eta}</span>
        </p>
      )}

      {isPayable(parcel) && (
        <div className="border-hairline mt-4 border-t pt-4">
          <p className="text-muted-foreground text-[13px]">Amount due</p>
          <p className="tabular text-[30px] leading-tight font-semibold tracking-[-0.02em]">{formatKES(parcel.amountChargedKes)}</p>
          <Button size="touch" className="mt-4 w-full" onClick={onPay}>
            Pay with M-Pesa
          </Button>
        </div>
      )}

      {parcel.status === "REGISTERED" && (
        <Link
          to="/addresses"
          className="mt-3 inline-flex min-h-11 items-center gap-1 text-[15px] font-medium underline-offset-4 hover:underline"
        >
          Not sent it yet? See your {countryName(parcel.originCountry)} address
          <ArrowRight className="size-4 shrink-0" />
        </Link>
      )}
    </section>
  );
}

function Timeline({ parcel }: { parcel: IParcel }) {
  const events = (parcel.events ?? []).slice().sort((a, b) => toNum(a.id) - toNum(b.id));
  if (events.length === 0) return null;
  return (
    <section className="border-hairline bg-card rounded-xl border p-5">
      <h2 className="type-headline">History</h2>
      <ol className="mt-4">
        {events.map((event, i) => {
          const latest = i === events.length - 1;
          return (
            <li key={toNum(event.id)} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span className={cn("mt-1.5 size-2.5 shrink-0 rounded-full", latest ? "bg-foreground" : "bg-foreground/30")} />
                {!latest && <span className="bg-hairline my-1 w-px flex-1" aria-hidden />}
              </div>
              <div className={cn("min-w-0", !latest && "pb-4")}>
                <p className={cn("text-[15px]", latest && "font-medium")}>{timelineLabel(event, parcel)}</p>
                <p className="text-muted-foreground text-[13px]">{formatDate(event.createdAt)}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function Details({ parcel }: { parcel: IParcel }) {
  const { copied, copy } = useCopy();
  return (
    <section className="border-hairline bg-card rounded-xl border p-5">
      <h2 className="type-headline">Details</h2>
      <dl className="divide-hairline mt-2 divide-y">
        {parcel.trackingNumber && (
          <Row label="Tracking number">
            <span className="flex items-center justify-between gap-3">
              <span className="wrap-anywhere">{parcel.trackingNumber}</span>
              <Button
                variant="outline"
                className="h-11 shrink-0 px-3 text-[14px]"
                onClick={() => copy(parcel.trackingNumber!)}
              >
                {copied ? <Check /> : <Copy />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </span>
          </Row>
        )}
        {parcel.labelCode && <Row label="ParcelPoint reference">{parcel.labelCode}</Row>}
        {parcel.expectedArrival && <Row label="Seller's delivery date">{formatDay(parcel.expectedArrival)}</Row>}
        {toNum(parcel.declaredValueKes) > 0 && <Row label="Value">{formatKES(parcel.declaredValueKes)}</Row>}
        <Row label="Added">{formatDay(parcel.createdAt)}</Row>
      </dl>
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="py-3 last:pb-0">
      <dt className="text-muted-foreground text-[13px]">{label}</dt>
      <dd className="text-[15px] font-medium">{children}</dd>
    </div>
  );
}
